import React from 'react';
import { i18n } from 'config/locales';
import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import { RelationshipsOtherNameModel } from 'forms/sections/relationships/shared/relationshipsOtherName';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { DateModel, NameModel } from 'forms/shared/types';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { setAttribute } from 'components/Form/shared/setAttribute';
import Field from 'components/Form/Field';
import Name from 'components/Form/Name';
import PVQName from 'components/Form/PVQName/Name';
import MaidenName from 'components/Form/MaidenName';
import DateRange from 'components/Form/DateRange';
import { isPVQForm } from 'helpers/form';
import NameOptions from 'components/Form/NameOptions';
import Textarea from 'components/Form/Textarea';
import NotApplicable from 'components/Form/NotApplicable';

interface Props extends Partial<RelationshipsOtherNameModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate;
  onError?: CommonOnError;
  required?: CommonRequired;
  scrollIntoView?: CommonScrollIntoView;
  applicantMinDate?: Date;
  Birthdate?: DateModel;
  forceMinDate?: boolean;
  attribute?: string;
  prefix?: string;
  /* CohabitantList has OtherName property; CivilUnion has Name property */
  nameAttribute?: string;
  formType?: string;
}

export default class OtherName extends ValidationElement<Props> {
  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<RelationshipsOtherNameModel>) => {
    this.props.onUpdate({
      [this.props.nameAttribute as keyof Props]: this.props[
        this.props.nameAttribute as keyof Props
      ],
      MaidenName: this.props.MaidenName,
      DatesUsed: this.props.DatesUsed,
      NameOptions: this.props.NameOptions, // pvq only
      NameOptionsExplanation: this.props.NameOptionsExplanation, // pvq only
      DatesNotApplicable: this.props.DatesNotApplicable, // pvq only
      ...queue,
    });
  };

  updateName = (values: NameModel) => {
    this.update({
      [this.props.nameAttribute as keyof Props]: values,
    });
  };

  updateMaidenName = (values: RelationshipsOtherNameModel['MaidenName']) => {
    this.update({
      MaidenName: values,
    });
  };

  updateDatesUsed = (values: RelationshipsOtherNameModel['DatesUsed']) => {
    this.update({
      DatesUsed: values,
    });
  };

  updateDatesNotApplicable = (values: RelationshipsOtherNameModel['DatesNotApplicable']) => {
    this.update({
      DatesNotApplicable: values,
    });
  };

  updateNameOptions = (values: RelationshipsOtherNameModel['NameOptions']) => {
    this.update({
      NameOptions: values,
    });
  };

  updateNameOptionsExplanation = (values: RelationshipsOtherNameModel['NameOptionsExplanation']) => {
    this.update({
      NameOptionsExplanation: values,
    });
  };

  getPrefixAndLabel = () => {
    const prefix = ['civilUnion', 'cohabitant'].includes(this.props.prefix)
      ? this.props.prefix
      : 'cohabitant';
    const prefixLabel = prefix === 'civilUnion' ? 'spouse' : 'cohabitant';
    return { prefix, prefixLabel };
  };

  render() {
    const minDate = this.props.applicantMinDate
      ? this.props.applicantMinDate
      : this.props.Birthdate;
    const forceMinDate = !!this.props.forceMinDate;

    const attribute = setAttribute(this.props.attribute, `items[${this.props.$index}].Item`);
    const { prefix, prefixLabel } = this.getPrefixAndLabel();
    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div>
        <Field
          title={i18n.t(`relationships.pvq.civilUnion.othernames.heading.name`)}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQName
            name="Othername"
            {...this.props.Name}
            aria-label={`Provide other name your ${prefixLabel} used`}
            onUpdate={this.updateName}
            onError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.${this.props.nameAttribute}`}
          />
        </Field>

        <Field
          title={i18n.t(`relationships.pvq.civilUnion.othernames.heading.describe`)}
          help="alias.maiden.help"
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.NameOptions`}
        >
          <NameOptions
            name="NameOptions"
            {...this.props.NameOptions}
            onUpdate={this.updateNameOptions}
            aria-label={`Provide your ${prefixLabel}'s maiden name`}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.NameOptions`}
          />
        </Field>
        {this.props.NameOptions?.value === 'Other' && (
          <div>
            <Field
              title="Please Explain"
              shrink
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.NameOptionsExplanation`}
            >
              <Textarea
                attribute={`${attribute}.NameOptionsExplanation`}
                name="NameOptionsExplanation"
                {...this.props.NameOptionsExplanation}
                className="name-options-explanation"
                onUpdate={this.updateNameOptionsExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </div>
        )}

        <Field
          title={i18n.t(`relationships.pvq.civilUnion.othernames.heading.used`)}
          adjustFor="daterange"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.DatesUsed`}
        >
          <NotApplicable
            attribute={`${attribute}.DatesNotApplicable`}
            name="DatesNotApplicable"
            className="date-notapplicable"
            {...this.props.DatesNotApplicable}
            label={i18n.t('relationships.pvq.civilUnion.notApplicable.idk')}
            or={i18n.m('relationships.pvq.civilUnion.notApplicable.or')}
            onError={this.props.onError}
            onUpdate={this.updateDatesNotApplicable}
          >
            <DateRange
              name="DatesUsed"
              prefix={this.props.prefix}
              {...this.props.DatesUsed}
              minDate={minDate}
              minDateEqualTo
              onUpdate={this.updateDatesUsed}
              onError={this.props.onError}
              className="datesused"
              required={this.props.required}
              hideFromDay
              hideToDay
              forceMinDate={forceMinDate}
              attribute={`${attribute}.DatesUsed`}
            />
          </NotApplicable>
        </Field>
      </div>
    ) : (
      <div>
        <Field
          title={i18n.t(`relationships.${prefix}.othernames.heading.name`)}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Name
            name="Othername"
            {...this.props[this.props.nameAttribute as keyof Props]}
            aria-label={`Provide other name your ${prefixLabel} used`}
            onUpdate={this.updateName}
            onError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.${this.props.nameAttribute}`}
          />
        </Field>

        <Field
          title={i18n.t(`relationships.${prefix}.othernames.heading.maiden`)}
          help="alias.maiden.help"
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.MaidenName`}
        >
          <MaidenName
            name="MaidenName"
            {...this.props.MaidenName}
            onUpdate={this.updateMaidenName}
            aria-label={`Provide your ${prefixLabel}'s maiden name`}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.MaidenName`}
          />
        </Field>

        <Field
          title={i18n.t(`relationships.${prefix}.othernames.heading.used`)}
          adjustFor="daterange"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.DatesUsed`}
        >
          <DateRange
            name="DatesUsed"
            prefix={this.props.prefix}
            {...this.props.DatesUsed}
            minDate={minDate}
            minDateEqualTo
            onUpdate={this.updateDatesUsed}
            onError={this.props.onError}
            className="datesused"
            required={this.props.required}
            hideFromDay
            hideToDay
            forceMinDate={forceMinDate}
            attribute={`${attribute}.DatesUsed`}
          />
        </Field>
      </div>
    );
  }
}

OtherName.defaultProps = {
  nameAttribute: 'Name',
  OtherName: {},
  Name: {},
  MaidenName: {},
  NameOptions: {}, // pvq only
  NameOptionsExplanation: {}, // pvq only
  DatesUsed: {},
  DatesNotApplicable: { applicable: true },
  onUpdate: (queue) => {},
  onError: (value, arr: ErrorCode) => arr,
};
